/**
 * The five migration phases and their colours, for screens that draw a roadmap.
 *
 * Colours copied verbatim from `screens/ImplementationRoadmap.tsx` PHASE_COLORS (lines 100–130),
 * so Roadmap Schedule's table and the Roadmap Review screen match Roadmap Visualization. That
 * file keeps its own copy, untouched.
 */
export const PHASES = [
  { key: 'assessment', name: 'Assessment & Planning', short: 'Assess' },
  { key: 'design', name: 'Design & Architecture', short: 'Design' },
  { key: 'migration', name: 'Migration Execution', short: 'Migrate' },
  { key: 'testing', name: 'Testing & Validation', short: 'Test' },
  { key: 'golive', name: 'Go-Live & Optimization', short: 'Go-Live and Support' },
] as const;

/**
 * Owner request 2026-11-04: on Roadmap Schedule the Go-Live phase reads "Go-Live and Support".
 * The stored phase name (effort plans, dependencies, colour keys) stays "Go-Live & Optimization";
 * this is the label only. Roadmap Visualization keeps its own copy and is unchanged.
 */
export const phaseLabel = (name: string | null | undefined): string =>
  name === 'Go-Live & Optimization' ? 'Go-Live and Support' : String(name ?? '');

export type PhaseKey = typeof PHASES[number]['key'];

export const PHASE_COLORS: Record<string, { primary: string; secondary: string; border: string; bg: string }> = {
  'Assessment & Planning': { primary: '#60A5FA', secondary: '#3B82F6', border: '#2563EB', bg: '#EFF6FF' },
  'Design & Architecture': { primary: '#C084FC', secondary: '#A855F7', border: '#9333EA', bg: '#FAF5FF' },
  'Migration Execution': { primary: '#4ADE80', secondary: '#22C55E', border: '#16A34A', bg: '#F0FDF4' },
  'Testing & Validation': { primary: '#FBBF24', secondary: '#F59E0B', border: '#D97706', bg: '#FFFBEB' },
  'Go-Live & Optimization': { primary: '#F87171', secondary: '#EF4444', border: '#DC2626', bg: '#FEF2F2' },
};

/**
 * The Roadmap Schedule GANTT's phase fills — copied verbatim from screens/RoadmapSchedule.tsx
 * PHASE_COLORS (lines 125–131). The Roadmap view uses these (owner request 2026-11-04: "use the
 * colors from the Gantt Chart view"), so its bars match the Gantt exactly.
 */
export const GANTT_PHASE_FILL: Record<string, string> = {
  'Assessment & Planning': '#BFDBFE',
  'Design & Architecture': '#E9D5FF',
  'Migration Execution': '#BBF7D0',
  'Testing & Validation': '#FDE68A',
  'Go-Live & Optimization': '#FECACA',
};

/** MM/DD/YYYY, as Roadmap Visualization's table prints dates. */
export function fmtMDY(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const [y, m, d] = ymd.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : '—';
}

/** Mon DD, YYYY — for headlines. */
export function fmtLong(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const dt = new Date(ymd.slice(0, 10) + 'T12:00:00');
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Business days between two YYYY-MM-DD dates, inclusive, weekends excluded. */
export function businessDays(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  const s = new Date(a + 'T12:00:00');
  const e = new Date(b + 'T12:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return null;
  let n = 0;
  for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) if (d.getDay() !== 0 && d.getDay() !== 6) n++;
  return n;
}

/** Calendar days from a to b. */
export function calendarDays(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  const s = new Date(a + 'T12:00:00').getTime();
  const e = new Date(b + 'T12:00:00').getTime();
  return isNaN(s) || isNaN(e) ? null : Math.round((e - s) / 86400000);
}
